/**
 * A figure written out, the way an Indian invoice writes it.
 *
 * <p>
 * <strong>A tax invoice carries the total in words, and the grouping is not the western one.</strong>
 * After the first thousand the digits group in twos, not threes: 1,25,000 is "one lakh twenty-five
 * thousand", and `Intl.NumberFormat` — which the rest of this product formats money with — has no
 * spelling mode at all, in any locale. So this is written out rather than borrowed.
 * </p>
 *
 * <p>
 * Pure and tested for the reason `GstPolicy` is: it is on a filed document, a customer reads it back
 * against the figure beside it, and "one lakh" against ₹10,000 is the kind of error that is only
 * ever noticed by the person you least want to notice it.
 * </p>
 */

const ones = [
  '',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
];

const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

/**
 * Nought to 99, which is the largest group the Indian system ever needs spelling — every place above
 * hundreds is two digits wide.
 */
function underHundred(value: number): string {
  if (value < 20) {
    return ones[value] ?? '';
  }

  const ten = tens[Math.floor(value / 10)] ?? '';
  const one = ones[value % 10] ?? '';

  return one ? `${ten}-${one}` : ten;
}

function underThousand(value: number): string {
  const hundreds = Math.floor(value / 100);
  const rest = value % 100;

  const parts = [
    hundreds > 0 ? `${ones[hundreds]} hundred` : '',
    rest > 0 ? underHundred(rest) : '',
  ].filter(Boolean);

  return parts.join(' and ');
}

/**
 * The places above a thousand, largest first. Two digits each — which is the whole difference from
 * the western grouping and the reason this cannot be a locale option.
 */
const scales: { value: number; name: string }[] = [
  { value: 10_000_000, name: 'crore' },
  { value: 100_000, name: 'lakh' },
  { value: 1_000, name: 'thousand' },
];

function whole(value: number): string {
  if (value === 0) {
    return 'zero';
  }

  const parts: string[] = [];
  let left = value;

  for (const scale of scales) {
    const count = Math.floor(left / scale.value);

    if (count > 0) {
      // Recursive, because a crore count is itself unbounded — 1,23,45,67,890 is "one hundred and
      // twenty-three crore …", and a flat lookup would cap the invoice at 99 crore.
      parts.push(`${whole(count)} ${scale.name}`);
      left %= scale.value;
    }
  }

  if (left > 0) {
    parts.push(underThousand(left));
  }

  return parts.join(' ');
}

/**
 * The amount in words, with its currency and its paise.
 *
 * <p>
 * <strong>Paise are spelled only when there are any</strong>, and "and fifty paise" rather than a
 * decimal, because the line exists to be read aloud against the figure. A round amount ends "only",
 * which is the convention every Indian invoice uses and which also does a real job: it says the
 * figure is finished, so nothing can be appended to it.
 * </p>
 *
 * <p>
 * <strong>A negative figure is spelled as one rather than silently dropped.</strong> Nothing on a
 * tax document should be negative — a credit note carries positive amounts and is a credit by its
 * kind — but a minus sign vanishing from the words while it stays on the figure is worse than an
 * ugly sentence.
 * </p>
 */
export function amountInWords(amount: number, currency = 'Rupees'): string {
  if (!Number.isFinite(amount)) {
    return '';
  }

  const negative = amount < 0;

  // Rounded to paise first, so 0.005 does not spell "zero" beside a figure printed as 0.01.
  const total = Math.round(Math.abs(amount) * 100);
  const rupees = Math.floor(total / 100);
  const paise = total % 100;

  const parts = [
    negative ? 'minus' : '',
    currency,
    whole(rupees),
    paise > 0 ? `and ${underHundred(paise)} paise` : '',
    'only',
  ].filter(Boolean);

  const sentence = parts.join(' ');

  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}
